import HistoricalTrackFilterReference from './HistoricalTrackFilterReference';
import TrackVenueRanking from './TrackVenueRanking';
import React, { useCallback, useEffect, useState } from 'react';
import {
  getBrainMeritPaperBuckets, BrainMeritPaperResponse, getCoreBrainAgreement, CoreBrainAgreementResponse, setBucketLiveControl, getBucketList, getBucketBets, getBucketRegistry, BucketOption, BucketBet, BucketBetsDetail, BucketRegistryResult, BucketRegistryRow,
  getSurfaceBandRegistry, getSurfaceBandRunners, SurfaceBandRow, SurfaceBandRunner,
  getFactorFamilies, getFactorFamilyRunners, FactorFamilyGroup, FactorFamilyStatBlock, FactorFamilyRunner,
} from '../api/strategy';
import './StrategyReports.css';
import { MyBucketsProvider, MyStakingPanel, MyBucketChoice, useMyBuckets } from './MyBucketSettings';

// Bet-bucket drill-down. Pick a bucket from the dropdown → every past bet in that bucket with its
// result, BSP, our rated price, CLV and whether it actually placed. Buckets are the criterion
// taxonomy (engine × race-state × selection criterion); the list only shows buckets that have bets.
//
// A second tab (Surface bands, added 2026-09-01) shows Paul's orthogonal turf/synthetic
// probability-band buckets — a whole-field classification, not just staged bets, and none of the
// 5 buckets ever hold cash. Same page family, same CSS, separate data source (strategy_runner_log
// rather than strategy_runs), so it's a sibling section rather than a new page.
const price = (v: number | null) => (v == null || Number.isNaN(v) ? '—' : v.toFixed(2));
const pct = (v: number | null) => (v == null || Number.isNaN(v) ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`);
const money = (v: number | null) => (v == null || Number.isNaN(v) ? '—' : `${v >= 0 ? '' : '-'}$${Math.abs(v).toFixed(2)}`);
const raceTime = (value?: string | null) => value && Number.isFinite(Date.parse(value))
  ? new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(value))
  : '—';
const whenDate = (d: string | null) => (d ? d : '—');

const resultPill = (r: BucketBet['result']) => {
  const map: Record<string, { bg: string; fg: string; txt: string }> = {
    win: { bg: '#dcfce7', fg: '#166534', txt: 'WON' },
    lose: { bg: '#fee2e2', fg: '#991b1b', txt: 'lost' },
    void: { bg: '#e5e7eb', fg: '#374151', txt: 'void' },
    pending: { bg: '#fef9c3', fg: '#854d0e', txt: 'pending' },
  };
  const s = map[r] || map.pending;
  return <span className="sr-pill" style={{ background: s.bg, color: s.fg }}>{s.txt}</span>;
};

const STATUS_STYLE: Record<string, { bg: string; fg: string; txt: string }> = {
  paper: { bg: '#fef9c3', fg: '#854d0e', txt: 'PAPER — lead LIVE candidate' },
  probation: { bg: '#e0e7ff', fg: '#3730a3', txt: 'PROBATION — track only' },
  exclusionary: { bg: '#e5e7eb', fg: '#374151', txt: 'EXCLUSIONARY — never bet' },
  unlisted: { bg: '#f3f4f6', fg: '#6b7280', txt: 'unlisted' },
};
const statusPill = (status: string) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unlisted;
  return <span className="sr-pill" style={{ background: s.bg, color: s.fg }}>{s.txt}</span>;
};

const EngineBuckets: React.FC<{ personal?: boolean }> = ({ personal = false }) => {
  const my = useMyBuckets();
  const historyFrom = '2026-08-01';
  const historyTo = new Intl.DateTimeFormat('en-CA', {timeZone:'Australia/Sydney',year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date());

  const [buckets, setBuckets] = useState<BucketOption[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [totalBets, setTotalBets] = useState(0);
  const [selected, setSelected] = useState('');
  const [sortBy, setSortBy] = useState<'bets' | 'roi' | 'days' | 'verified' | 'avg_winner'>('roi');
  const [bets, setBets] = useState<BucketBet[]>([]);
  const [detail, setDetail] = useState<BucketBetsDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Control failures are visible and retryable without blocking the independent history request.
  const [savingMode, setSavingMode] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);
  const [registry, setRegistry] = useState<BucketRegistryResult | null>(null);
  const [registryLoading, setRegistryLoading] = useState(true);
  const [registryError, setRegistryError] = useState<string | null>(null);
  const loadRegistry = useCallback(async () => {
    setRegistryLoading(true); setRegistryError(null);
    try { setRegistry(await getBucketRegistry()); }
    catch (e: any) { setRegistryError(e?.message || 'Could not load Live/Paper controls'); }
    finally { setRegistryLoading(false); }
  }, []);

  // Load the dropdown once. n_bets = the whole slate over the period (every bucket combined).
  useEffect(() => {
    (async () => {
      try { const list = await getBucketList(historyFrom, historyTo); setBuckets(list.buckets); setTotalBets(list.n_bets); }
      catch (e: any) { setError(e?.message || 'Could not load buckets'); }
      finally { setListLoading(false); }
    })();
    void loadRegistry();
  }, [loadRegistry, historyTo]);

  const registryFor = (policyId: string): BucketRegistryRow | undefined =>
    registry?.buckets.find((b) => b.policy_id === policyId);

  useEffect(() => {
    setModeError(null);
  }, [selected]);

  const toggleMode = async () => {
    const id = selected;
    const current = registryFor(id);
    if (!current || !registry?.can_edit || current.can_edit === false || registryLoading || registryError) return;
    setSavingMode(true); setModeError(null);
    try {
      const row = await setBucketLiveControl(current, !current.live_enabled);
      setRegistry(old => old ? { ...old, buckets: [...old.buckets.filter(b => b.policy_id !== id), {...current, ...row}] } : old);
    } catch (e: any) { setModeError(e?.message || 'Could not save mode'); }
    finally { setSavingMode(false); }
  };

  // Load the selected bucket's bets.
  useEffect(() => {
    if (!selected) { setBets([]); setDetail(null); return; }
    let active = true;
    setLoading(true); setError(null); setBets([]); setDetail(null);
    getBucketBets(selected, historyFrom, historyTo)
      .then((d) => { if (active) { setBets(d.bets); setDetail(d); } })
      .catch((e: any) => { if (active) setError(e?.message || 'Could not load this bucket'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selected, historyTo]);

  const selectedCoverage = buckets.find((b) => b.policy_id === selected)?.history_coverage;
  const settled = bets.filter((b) => b.result === 'win' || b.result === 'lose');
  const wins = settled.filter((b) => b.result === 'win').length;
  const placedN = bets.filter((b) => b.placed).length;
  const pendingN = detail?.pending_n ?? bets.filter((b) => b.result === 'pending').length;
  const clvVals = bets.map((b) => b.clv).filter((x): x is number => x != null);
  const avgClv = clvVals.length ? clvVals.reduce((a, b) => a + b, 0) / clvVals.length : null;

  // Sorted client-side (the full list is already loaded) — ROI puts buckets with nothing
  // settled/priced yet last rather than at the top, since a null ROI isn't "best".
  // Keep registered lanes reachable even before they have their first history row.
  const allBuckets: BucketOption[] = [...buckets, ...(registry?.buckets || [])
    .filter(r => r.control_kind !== 'manual' && !buckets.some(b => b.policy_id === r.policy_id))
    .map(r => ({policy_id:r.policy_id, label:r.label || r.policy_id, n:0, settled_n:0}))];
  if (personal && !allBuckets.some(b => b.policy_id === 'FAV_STAKES_3PLUS|S-|')) allBuckets.push({ policy_id: 'FAV_STAKES_3PLUS|S-|', label: 'Favourite · 3yo+ Listed/Group (T−1 lock)', n: 0, settled_n: 0 } as BucketOption);
  const sortedBuckets = allBuckets.sort((a, b) => (
    (b.policy_id === 'VALUE_S4_SETUP_TOP2|S4|' ? 1 : 0) - (a.policy_id === 'VALUE_S4_SETUP_TOP2|S4|' ? 1 : 0) ||
    (sortBy === 'roi'
      ? (b.roi ?? -Infinity) - (a.roi ?? -Infinity) || b.n - a.n
      : sortBy === 'verified' ? (b.verified_roi ?? -Infinity) - (a.verified_roi ?? -Infinity) || (b.verified_n ?? 0) - (a.verified_n ?? 0)
      : sortBy === 'avg_winner' ? (b.avg_winner_bsp ?? -Infinity) - (a.avg_winner_bsp ?? -Infinity) || b.n - a.n
      : sortBy === 'days' ? (a.max_drawdown_days ?? Infinity) - (b.max_drawdown_days ?? Infinity) || b.n-a.n : b.n - a.n
  )));

  return (
    <>
      <div className="sr-head">
        {totalBets > 0 && (
          <span className="rc-note" style={{ margin: 0 }}>
            {totalBets} total bets in the slate from 1 August 2026 to {historyTo} · {allBuckets.length} bucket{allBuckets.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {buckets.some((b) => b.history_coverage && b.history_coverage.status !== 'COMPLETE') && (
        <p className="rc-note" role="status">2026 paper history has been rebuilt with the updated Core on available data. This view is filtered from 1 August 2026 to today. Some buckets lack required inputs or are blocked by current settings. ROI is a BSP benchmark; recorded-quote P&amp;L is shown separately. Actual placed bets keep their original records.</p>
      )}

      <p className="rc-note">Max DD shows bets and calendar days until the previous profit peak is recovered. Days without bets count; same-day recovery is 1 day. Bets and days are separate maxima and may come from different drawdowns. Flat-stake BSP after commission, within this reporting period. Verified forward ROI counts only bets that were actually placed or recorded before the race; backtest, replay and rebuilt rows are excluded.</p>
      {personal && <MyStakingPanel labels={Object.fromEntries(allBuckets.map(b => [b.policy_id, b.label]))} />}
      <div className="sr-card" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', padding: 12, margin: '10px 0' }}>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
          Bucket
          <select disabled={savingMode} value={selected} onChange={(e) => setSelected(e.target.value)} style={{ minWidth: 340, padding: 6, marginTop: 4 }}>
            <option value="">— select a bucket —</option>
            {!personal && <option value="__live_by_time__">live buckets sorted by time</option>}
            {sortedBuckets.map((b) => {
              const r = registryFor(b.policy_id);
              return (
                <option key={b.policy_id} value={b.policy_id}>
                  {personal ? (my?.config?.buckets?.[b.policy_id]?.enabled ? '✓ MY BOT' : '○') : (r?.control_kind === 'manual' ? 'Manual' : registryLoading || registryError || !r ? 'Status unavailable' : r.live_enabled ? '● LIVE' : '○ paper')} — {b.label} {'\u2014'} Max DD: {b.max_drawdown_bets == null ? 'unavailable' : b.max_drawdown_bets + ' bets'} / {b.max_drawdown_days == null ? 'days unavailable' : b.max_drawdown_days + ' days'} ({b.n} bet{b.n === 1 ? '' : 's'}{b.settled_n < b.n ? `, ${b.settled_n} settled` : ''}{b.backtest_n ? `, ${b.backtest_n} historical` : ''}{b.backtest_priced_n ? `, ${b.backtest_priced_n} priced` : ''}{b.forward_n ? `, ${b.forward_n} forward` : ''})
                  {b.history_coverage ? ' - ' + b.history_coverage.status.replace(/_/g, ' ').toLowerCase() : ''}
                  {b.roi != null ? ` — ${b.roi_label || 'ROI'} ${b.roi >= 0 ? '+' : ''}${b.roi.toFixed(1)}%` : ''}
                  {b.avg_winner_bsp != null ? ` — avg winner $${b.avg_winner_bsp.toFixed(2)}` : ''}
                  {b.verified_n ? ` — verified forward ${(b.verified_roi ?? 0) >= 0 ? '+' : ''}${(b.verified_roi ?? 0).toFixed(1)}% on ${b.verified_n}` : ' — no verified forward bets'}
                  {b.last_bet_date ? ` — last bet ${b.last_bet_date}` : ''}
                  {b.retirement_note ? ' — RETIRED' : ''}
                </option>
              );
            })}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
          Sort by
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'bets' | 'roi' | 'days' | 'verified' | 'avg_winner')} style={{ minWidth: 140, padding: 6, marginTop: 4 }}>
            <option value="roi">ROI (highest first)</option>
            <option value="verified">Verified forward ROI (highest first)</option>
            <option value="avg_winner">Avg winner (highest first)</option>
            <option value="bets">Bet count</option><option value="days">Drawdown days (shortest first)</option>
          </select>
        </label>
        {listLoading ? <span className="rc-note" style={{ margin: 0 }} role="status">Loading buckets and drawdown history… (takes a few seconds)</span>
          : buckets.length === 0 && !error && <span className="rc-note" style={{ margin: 0 }}>No buckets with bets yet.</span>}
      </div>

      {selected === '__live_by_time__' && <p className="rc-note">All bets from currently live-enabled buckets, including paper bets. Latest date first, then race time from earliest to latest (Sydney time). Missing times are shown as — and listed last within each date.</p>}
      {selectedCoverage && <p className="rc-note" role="status">{selectedCoverage.note}</p>}
      {!personal && registryError && <div role="alert" className="sr-error">
        Live/Paper controls could not load: {registryError}.{' '}
        <button type="button" disabled={registryLoading} onClick={loadRegistry}>Retry controls</button>
      </div>}
      {personal && selected && selected !== '__live_by_time__' && <MyBucketChoice policyId={selected} />}
      {!personal && selected && selected !== '__live_by_time__' && (() => {
        const r = registryFor(selected);
        const unavailable = registryLoading || !!registryError || !r;
        const editable = !!registry?.can_edit && r?.can_edit !== false;
        const live = !unavailable && r?.live_enabled;
        return (
          <div className="sr-card" aria-label="Bucket live control" style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', margin: '0 0 10px' }}>
            <span className="sr-pill" style={{ background: live ? '#dcfce7' : '#f3f4f6', color: live ? '#166534' : '#6b7280' }}>
              {registryLoading ? 'Loading Live/Paper controls…' : unavailable ? 'Control unavailable' : r?.control_kind === 'manual' ? 'MANUAL BETS' : live ? 'ENABLED — feeds Auto' : 'PAPER ONLY — automatic push off'}
            </span>
            {r?.control_kind !== 'manual' && <button type="button" role="switch" aria-checked={!!live}
              aria-label="Live betting for selected bucket" disabled={savingMode || unavailable || !editable}
              onClick={toggleMode}
              style={{ padding: '8px 18px', borderRadius: 20, border: '1px solid #94a3b8', cursor: savingMode || unavailable || !editable ? 'default' : 'pointer', background: live ? '#dcfce7' : '#f3f4f6' }}>
              {savingMode ? 'Saving…' : unavailable ? 'Unavailable' : live ? 'Live' : 'Paper'}
            </button>}
            {r?.control_note && <span className="rc-note">{r.control_note}</span>}
            {!unavailable && r?.control_kind !== 'manual' && <>
              <span className="rc-note">Stakes and bucket multipliers are set on the Auto page.</span>
              <span className="rc-note">{editable ? 'Click to switch. Applies to new orders; existing placed bets remain.' : 'An authorised administrator can change this control.'}</span>
              <span className="rc-note">Real money requires Auto Live mode and Arm.</span>
            </>}
            {!registryLoading && !registryError && !r && <span className="rc-note">No automatic control is registered for this history bucket.</span>}
            {modeError && <span role="alert" className="sr-error">{modeError}</span>}
            <button type="button" disabled={registryLoading || savingMode} onClick={loadRegistry}>Refresh controls</button>
          </div>
        );
      })()}

      {error && <p className="sr-error">{error}</p>}

      {selected && detail?.retirement_note && (
        <div className="sr-card" style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: '10px 14px', margin: '0 0 10px' }}>
          <strong style={{ color: '#92400e' }}>Retired, not dead:</strong>{' '}
          <span style={{ color: '#78350f' }}>{detail.retirement_note}</span>
          {detail.last_bet_date && (
            <div className="rc-note" style={{ margin: '4px 0 0' }}>Last real bet in this bucket: {detail.last_bet_date}.</div>
          )}
        </div>
      )}

      {selected && !loading && bets.length > 0 && (
        <>
          <div className="sr-tiles">
            <div className="sr-tile" title={detail?.drawdown_days_basis}>
              <div className="sr-tile-val">{detail?.max_drawdown_days == null ? 'Unavailable' : detail.max_drawdown_days + ' days'}</div>
              <div className="sr-tile-label">longest drawdown by time{detail?.max_drawdown_days_bets != null ? ' ('+detail.max_drawdown_days_bets+' bets)' : ''}</div>
            </div>
            <div className="sr-tile" title={detail?.drawdown_bets_basis}>
              <div className="sr-tile-val">{detail?.max_drawdown_bets == null ? 'Unavailable' : detail.max_drawdown_bets + ' bets'}</div>
              <div className="sr-tile-label">longest drawdown by bets{detail?.max_drawdown_bets_days != null ? ' ('+detail.max_drawdown_bets_days+' days)' : ''}</div>
            </div>
            <div className="sr-tile" title={detail?.drawdown_days_basis}>
              <div className="sr-tile-val">{detail?.current_drawdown_days == null ? 'Unavailable' : detail.current_drawdown_days + ' days'}</div>
              <div className="sr-tile-label">unrecovered: {detail?.current_drawdown_bets ?? '-'} bets{detail?.drawdown_as_of ? ' at '+detail.drawdown_as_of : ''}</div>
            </div>

            <div className="sr-tile"><div className="sr-tile-val">{bets.length}</div><div className="sr-tile-label">bets in bucket</div></div>
            <div className="sr-tile"><div className="sr-tile-val">{placedN}</div><div className="sr-tile-label">actually placed</div></div>
            <div className="sr-tile"><div className="sr-tile-val">{settled.length ? `${(wins / settled.length * 100).toFixed(1)}%` : '\u2014'}</div><div className="sr-tile-label">strike rate ({wins}/{settled.length})</div></div>
            <div className="sr-tile" title="Average BSP across settled bets with a valid BSP">
              <div className="sr-tile-val">{detail?.avg_bsp == null ? '\u2014' : `$${detail.avg_bsp.toFixed(2)}`}</div><div className="sr-tile-label">avg price (BSP)</div>
            </div>
            <div className="sr-tile" title="Average BSP of the settled bets that won">
              <div className="sr-tile-val">{detail?.avg_winner_bsp == null ? '\u2014' : `$${detail.avg_winner_bsp.toFixed(2)}`}</div><div className="sr-tile-label">avg winner (BSP){detail?.winners_priced_n ? ' (' + detail.winners_priced_n + ' wins)' : ''}</div>
            </div>
            <div className="sr-tile" title={detail?.drawdown_basis || 'Daily closing drawdown in flat-stake units'}>
              <div className="sr-tile-val">{detail?.max_drawdown_units == null ? '\u2014' : `${detail.max_drawdown_units.toFixed(2)}u`}</div><div className="sr-tile-label">max drawdown (daily)</div>
            </div>
            <div className="sr-tile">
              <div className="sr-tile-val" style={{ color: detail?.roi == null ? undefined : detail.roi >= 0 ? '#166534' : '#991b1b' }}>{pct(detail?.roi ?? null)}</div>
              <div className="sr-tile-label">{buckets.find(b=>b.policy_id===selected)?.roi_label || 'ROI'} (flat, priced)</div>
            </div>
            {detail?.forward_roi != null && <div className="sr-tile"><div className="sr-tile-val">{pct(detail.forward_roi)}</div><div className="sr-tile-label">forward paper ROI (flat)</div></div>}
            <div className="sr-tile"><div className="sr-tile-val">{pct(avgClv)}</div><div className="sr-tile-label">avg CLV</div></div>
          </div>
          {detail?.forward_drawdown?.current_drawdown_days != null && <p className="rc-note">Forward paper: current drawdown {detail.forward_drawdown.current_drawdown_bets} bets / {detail.forward_drawdown.current_drawdown_days} days; longest {detail.forward_drawdown.max_drawdown_days} days. As of {detail.forward_drawdown.drawdown_as_of}.</p>}
          {(detail?.graded_live ?? 0) > 0 && (
            <p className="rc-note" style={{ margin: '0 0 4px', color: '#166534' }}>
              {detail!.graded_live} result{detail!.graded_live === 1 ? '' : 's'} recovered from the settled Betfair market
              (the settlement worker hadn’t stamped them).
            </p>
          )}
          {pendingN > 0 && (
            <p className="rc-note" style={{ margin: '0 0 8px' }}>
              {pendingN} of {bets.length} bets are still <b>pending</b> — no settled Betfair market found for them yet
              (Result / BSP / CLV / ROI fill once the race settles).
            </p>
          )}
        </>
      )}

      {selected && loading && <p className="rc-note">Loading…</p>}
      {selected && !loading && bets.length === 0 && !error && <p className="sr-empty">No bets in this bucket.</p>}

      {selected && !loading && bets.length > 0 && (
        <table role="table" className="sr-table mobile-card-table">
          <thead role="rowgroup"><tr role="row">
            <th role="columnheader">Date</th><th role="columnheader">Time (Sydney)</th><th role="columnheader">Race</th><th role="columnheader">Runner</th><th role="columnheader">Market</th><th role="columnheader">Engine</th>
            {selected === '__live_by_time__' && <th role="columnheader">Bucket</th>}
            <th role="columnheader">Placed</th><th role="columnheader">Result</th><th role="columnheader">BSP</th><th role="columnheader">Rated</th><th role="columnheader">CLV</th><th role="columnheader">P&amp;L</th>
          </tr></thead>
          <tbody role="rowgroup">
            {bets.map((b, i) => (
              <tr role="row" key={i}>
                <td role="cell" data-label="Date">{whenDate(b.date)}</td>
                <td role="cell" data-label="Time (Sydney)">{raceTime(b.start_time)}</td>
                <td role="cell" data-label="Race">{b.track || '—'}{b.race != null ? ` R${b.race}` : ''}</td>
                <td role="cell" data-label="Runner">{b.runner || '—'}</td>
                <td role="cell" data-label="Market">{b.market_type || '—'}</td>
                <td role="cell" data-label="Engine">{b.engine || '—'}</td>
                {selected === '__live_by_time__' && <td role="cell" data-label="Bucket">{b.bucket_label || b.policy_id || '—'}</td>}
                <td role="cell" data-label="Placed">
                  <span className="sr-pill" style={{ background: b.placed ? '#dbeafe' : '#f3f4f6', color: b.placed ? '#1e40af' : '#6b7280' }}>
                    {b.placed ? 'placed' : b.research_backtest_kind === 'SOURCE_UNION_BENCHMARK' ? 'source replay' : b.retrospective ? 'backtest' : 'paper'}
                  </span>
                </td>
                <td role="cell" data-label="Result">{resultPill(b.result)}</td>
                <td role="cell" data-label="BSP" className="sr-num">{price(b.bsp)}</td>
                <td role="cell" data-label="Rated" className="sr-num">{price(b.rated_price)}</td>
                <td role="cell" data-label="CLV" className="sr-num" style={{ color: b.clv == null ? undefined : b.clv >= 0 ? '#166534' : '#991b1b' }}>{pct(b.clv)}</td>
                <td role="cell" data-label="P&L" className="sr-num" style={{ color: b.pnl == null ? undefined : b.pnl >= 0 ? '#166534' : '#991b1b' }}>{money(b.pnl)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

const SurfaceBands: React.FC = () => {
  const [registry, setRegistry] = useState<SurfaceBandRow[]>([]);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [selected, setSelected] = useState('');
  const [runners, setRunners] = useState<SurfaceBandRunner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSurfaceBandRegistry()
      .then((d) => { setEnabled(d.enabled); setRegistry(d.buckets); })
      .catch((e: any) => setError(e?.message || 'Could not load surface bands'));
  }, []);

  useEffect(() => {
    if (!selected) { setRunners([]); return; }
    setLoading(true); setError(null);
    getSurfaceBandRunners(selected)
      .then((d) => setRunners(d.runners))
      .catch((e: any) => setError(e?.message || 'Could not load this bucket'))
      .finally(() => setLoading(false));
  }, [selected]);

  const selectedRow = registry.find((r) => r.key === selected) || null;
  const settled = runners.filter((r) => r.result === 'win' || r.result === 'lose');
  const wins = settled.filter((r) => r.result === 'win').length;

  if (enabled === false) {
    return <p className="sr-empty">Surface-band tagging is OFF (STRATEGY_SURFACE_BAND_ENABLED). Turn it on in Settings to start tracking Paul's turf/synthetic buckets.</p>;
  }

  return (
    <>
      <div className="sr-card" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', padding: 12, margin: '10px 0' }}>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
          Bucket
          <select value={selected} onChange={(e) => setSelected(e.target.value)} style={{ minWidth: 380, padding: 6, marginTop: 4 }}>
            <option value="">— select a bucket —</option>
            {registry.map((b) => (
              <option key={b.key} value={b.key}>
                {b.status.toUpperCase()} — {b.label} ({b.n} runner{b.n === 1 ? '' : 's'}{b.strike != null ? `, strike ${(b.strike * 100).toFixed(1)}%` : ''})
              </option>
            ))}
          </select>
        </label>
      </div>

      {selectedRow && (
        <div className="sr-card" style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', margin: '0 0 10px' }}>
          {statusPill(selectedRow.status)}
          <span className="rc-note" style={{ margin: 0 }}>
            N={selectedRow.n} · strike {selectedRow.strike == null ? '—' : `${(selectedRow.strike * 100).toFixed(1)}%`}
            {' · '}ROI {pct(selectedRow.roi)} · P&amp;L {money(selectedRow.pnl)}
            {selectedRow.key === 'TURF_SWEET_SPOT' ? ' (Rule B picks: one bet per race, highest model_prob)' : ' (whole population)'}
          </span>
          {selectedRow.promotion && (
            <span
              className="sr-pill"
              style={{
                background: selectedRow.promotion.verdict === 'PROMOTE' ? '#dcfce7' : selectedRow.promotion.verdict === 'CULL' ? '#fee2e2' : '#fef9c3',
                color: selectedRow.promotion.verdict === 'PROMOTE' ? '#166534' : selectedRow.promotion.verdict === 'CULL' ? '#991b1b' : '#854d0e',
              }}
            >
              {selectedRow.promotion.verdict} — {selectedRow.promotion.reason}
            </span>
          )}
        </div>
      )}

      {error && <p className="sr-error">{error}</p>}
      {selected && loading && <p className="rc-note">Loading…</p>}
      {selected && !loading && runners.length === 0 && !error && <p className="sr-empty">No runners tagged in this bucket yet.</p>}

      {selected && !loading && runners.length > 0 && (
        <>
          <div className="sr-tiles">
            <div className="sr-tile"><div className="sr-tile-val">{runners.length}</div><div className="sr-tile-label">runners tagged</div></div>
            <div className="sr-tile"><div className="sr-tile-val">{settled.length ? `${wins}/${settled.length}` : '—'}</div><div className="sr-tile-label">strike (won / settled)</div></div>
          </div>
          <table role="table" className="sr-table mobile-card-table">
            <thead role="rowgroup"><tr role="row">
              <th role="columnheader">Date</th><th role="columnheader">Race</th><th role="columnheader">Runner</th><th role="columnheader">Surface</th>
              <th role="columnheader">Model rank</th><th role="columnheader">Market rank</th><th role="columnheader">Model prob</th><th role="columnheader">Model fair</th>
              {selectedRow?.key === 'TURF_SWEET_SPOT' && <th role="columnheader">Pick</th>}
              <th role="columnheader">Result</th><th role="columnheader">BSP</th><th role="columnheader">P&amp;L</th>
            </tr></thead>
            <tbody role="rowgroup">
              {runners.map((r, i) => (
                <tr role="row" key={i} style={r.turf_sweet_spot_pick ? { fontWeight: 600 } : undefined}>
                  <td role="cell" data-label="Date">{whenDate(r.date)}</td>
                  <td role="cell" data-label="Race">{r.track || '—'}{r.race != null ? ` R${r.race}` : ''}</td>
                  <td role="cell" data-label="Runner">{r.runner || '—'}</td>
                  <td role="cell" data-label="Surface">{r.surface || '—'}</td>
                  <td role="cell" data-label="Model rank" className="sr-num">{r.model_rank ?? '—'}</td>
                  <td role="cell" data-label="Market rank" className="sr-num">{r.market_rank ?? '—'}</td>
                  <td role="cell" data-label="Model prob" className="sr-num">{r.model_prob == null ? '—' : `${r.model_prob.toFixed(1)}%`}</td>
                  <td role="cell" data-label="Model fair" className="sr-num">{price(r.model_fair)}</td>
                  {selectedRow?.key === 'TURF_SWEET_SPOT' && (
                    <td role="cell" data-label="Pick">{r.turf_sweet_spot_pick ? <span className="sr-pill" style={{ background: '#dcfce7', color: '#166534' }}>pick</span> : '—'}</td>
                  )}
                  <td role="cell" data-label="Result">{resultPill(r.result as BucketBet['result'])}</td>
                  <td role="cell" data-label="BSP" className="sr-num">{price(r.bsp)}</td>
                  <td role="cell" data-label="P&L" className="sr-num" style={{ color: r.pnl == null ? undefined : r.pnl >= 0 ? '#166534' : '#991b1b' }}>{money(r.pnl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};

const WINDOW_LABEL: Record<string, string> = { last_50: 'Last 50', last_100: 'Last 100', entire_sample: 'Entire sample' };

const FactorFamilies: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [groups, setGroups] = useState<FactorFamilyGroup[]>([]);
  const [family, setFamily] = useState('');
  const [bucket, setBucket] = useState('');
  const [win, setWin] = useState<'last_50' | 'last_100' | 'entire_sample'>('entire_sample');
  const [runners, setRunners] = useState<FactorFamilyRunner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFactorFamilies()
      .then((d) => { setEnabled(d.enabled); setGroups(d.families); })
      .catch((e: any) => setError(e?.message || 'Could not load factor families'));
  }, []);

  useEffect(() => {
    if (!bucket) { setRunners([]); return; }
    setLoading(true); setError(null);
    getFactorFamilyRunners(bucket)
      .then((d) => setRunners(d.runners))
      .catch((e: any) => setError(e?.message || 'Could not load this bucket'))
      .finally(() => setLoading(false));
  }, [bucket]);

  const selectedGroup = groups.find((g) => g.family === family) || null;
  const selectedBucket = selectedGroup?.buckets.find((b) => b.key === bucket) || null;
  const stats: FactorFamilyStatBlock | null = selectedBucket ? selectedBucket[win] : null;
  const settled = runners.filter((r) => r.result === 'win' || r.result === 'lose');
  const wins = settled.filter((r) => r.result === 'win').length;

  if (enabled === false) {
    return <p className="sr-empty">Factor-family tagging is OFF (STRATEGY_FACTOR_FAMILIES_ENABLED). Turn it on in Settings to start tracking Paul's 9 research families.</p>;
  }

  return (
    <>
      <div className="sr-card" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', padding: 12, margin: '10px 0' }}>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
          Family
          <select value={family} onChange={(e) => { setFamily(e.target.value); setBucket(''); }} style={{ minWidth: 260, padding: 6, marginTop: 4 }}>
            <option value="">— select a family —</option>
            {groups.map((g) => <option key={g.family} value={g.family}>{g.family}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
          Bucket
          <select value={bucket} onChange={(e) => setBucket(e.target.value)} disabled={!selectedGroup} style={{ minWidth: 260, padding: 6, marginTop: 4 }}>
            <option value="">— select a bucket —</option>
            {selectedGroup?.buckets.map((b) => (
              <option key={b.key} value={b.key}>{b.key} ({b.entire_sample.n} settled)</option>
            ))}
          </select>
        </label>
        {selectedBucket && (
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600 }}>
            Window
            <select value={win} onChange={(e) => setWin(e.target.value as any)} style={{ minWidth: 140, padding: 6, marginTop: 4 }}>
              {Object.entries(WINDOW_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </label>
        )}
      </div>

      {error && <p className="sr-error">{error}</p>}

      {stats && (
        <div className="sr-tiles">
          <div className="sr-tile"><div className="sr-tile-val">{stats.n}</div><div className="sr-tile-label">settled</div></div>
          <div className="sr-tile"><div className="sr-tile-val">{stats.strike == null ? '—' : `${(stats.strike * 100).toFixed(1)}%`}</div><div className="sr-tile-label">strike</div></div>
          <div className="sr-tile"><div className="sr-tile-val">{stats.expected_strike == null ? '—' : `${(stats.expected_strike * 100).toFixed(1)}%`}</div><div className="sr-tile-label">expected strike</div></div>
          <div className="sr-tile"><div className="sr-tile-val">{stats.actual_over_expected == null ? '—' : stats.actual_over_expected.toFixed(2)}</div><div className="sr-tile-label">actual/expected</div></div>
          <div className="sr-tile"><div className="sr-tile-val" style={{ color: stats.net_roi == null ? undefined : stats.net_roi >= 0 ? '#166534' : '#991b1b' }}>{pct(stats.net_roi == null ? null : stats.net_roi * 100)}</div><div className="sr-tile-label">net ROI</div></div>
          <div className="sr-tile"><div className="sr-tile-val" style={{ color: stats.trimmed_roi == null ? undefined : stats.trimmed_roi >= 0 ? '#166534' : '#991b1b' }}>{pct(stats.trimmed_roi == null ? null : stats.trimmed_roi * 100)}</div><div className="sr-tile-label">trimmed ROI (10%)</div></div>
          <div className="sr-tile"><div className="sr-tile-val">{money(stats.cumulative_pnl)}</div><div className="sr-tile-label">cumulative P&amp;L</div></div>
          <div className="sr-tile"><div className="sr-tile-val">{(stats.max_drawdown * 100).toFixed(1)}%</div><div className="sr-tile-label">max drawdown</div></div>
          <div className="sr-tile">
            <div className="sr-tile-val">{stats.bootstrap_ci_90.lo == null ? '—' : `${pct(stats.bootstrap_ci_90.lo * 100)} to ${pct(stats.bootstrap_ci_90.hi! * 100)}`}</div>
            <div className="sr-tile-label">90% bootstrap ROI CI</div>
          </div>
        </div>
      )}

      {bucket && loading && <p className="rc-note">Loading…</p>}
      {bucket && !loading && runners.length === 0 && !error && <p className="sr-empty">No runners tagged in this bucket yet.</p>}

      {bucket && !loading && runners.length > 0 && (
        <>
          <p className="rc-note" style={{ margin: '0 0 8px' }}>{runners.length} runners tagged all-time · {settled.length ? `${wins}/${settled.length}` : '—'} strike (won / settled)</p>
          <table role="table" className="sr-table mobile-card-table">
            <thead role="rowgroup"><tr role="row">
              <th role="columnheader">Date</th><th role="columnheader">Race</th><th role="columnheader">Runner</th><th role="columnheader">Result</th><th role="columnheader">BSP</th><th role="columnheader">P&amp;L</th>
            </tr></thead>
            <tbody role="rowgroup">
              {runners.map((r, i) => (
                <tr role="row" key={i}>
                  <td role="cell" data-label="Date">{whenDate(r.date)}</td>
                  <td role="cell" data-label="Race">{r.track || '—'}{r.race != null ? ` R${r.race}` : ''}</td>
                  <td role="cell" data-label="Runner">{r.runner || '—'}</td>
                  <td role="cell" data-label="Result">{resultPill(r.result as BucketBet['result'])}</td>
                  <td role="cell" data-label="BSP" className="sr-num">{price(r.bsp)}</td>
                  <td role="cell" data-label="P&L" className="sr-num" style={{ color: r.pnl == null ? undefined : r.pnl >= 0 ? '#166534' : '#991b1b' }}>{money(r.pnl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};

const BrainMeritPaper: React.FC = () => {
  const [data, setData] = useState<BrainMeritPaperResponse | null>(null);
  const [policy, setPolicy] = useState('');
  const [error, setError] = useState('');
  const [agreement, setAgreement] = useState<CoreBrainAgreementResponse | null>(null);
  useEffect(() => { getCoreBrainAgreement().then(setAgreement).catch(() => {}); }, []);
  useEffect(() => { getBrainMeritPaperBuckets(policy || undefined).then(setData).catch(e => setError(e.message)); }, [policy]);
  return <section>
    <h2>Brain merit - On-Base S1</h2>
    <p className="rc-note">Paper only. Historical inputs from 4 June 2026 are a retrospective replay using a model trained through 15 September. No money was placed. Only reconciled BSP results count in ROI; positive profit has 10% commission.</p>
    {error && <p className="sr-error">{error}</p>}
    {agreement && <div className="sr-card" style={{ padding: 12, marginBottom: 12 }}>
      <h3>Core + Brain agreement: paper cull and stake replay</h3>
      <p className="rc-note">Published Core replay, {agreement.replay_period.from} to {agreement.replay_period.to}. Flat $100 base bet; 10% commission at reconciled WIN BSP. These are paper scenarios, not executed stakes or a compounded bank.</p>
      <p className="rc-note">Same runner, same criterion: {agreement.historical.counts.SAME_CRITERION || 0}; same runner, different criterion: {agreement.historical.counts.CROSS_CRITERION || 0}. Forward snapshots: {agreement.prospective.picks}, settled: {agreement.prospective.settled}, unavailable daily floor: {agreement.prospective.counts.UNKNOWN_DAILY_FLOOR || 0}. Worker last checked: {agreement.health?.at ? new Date(agreement.health.at).toLocaleString('en-AU') : 'not yet'}; Core picks in entry window: {agreement.health?.checked ?? 0}.</p>
      <table role="table" className="sr-table mobile-card-table"><thead role="rowgroup"><tr role="row"><th role="columnheader">Scenario</th><th role="columnheader">Settled bets</th><th role="columnheader">Turnover</th><th role="columnheader">Profit</th><th role="columnheader">ROI</th></tr></thead>
      <tbody role="rowgroup">{[
        ['All Core at $100', 'core_all'], ['Cull to same-runner agreement', 'cull_to_agreement'],
        ['All Core, agreements at $150', 'agree_1_5x'], ['All Core, agreements at $200', 'agree_2x'],
        ['Cull and stake agreements at $150', 'cull_and_1_5x'], ['Cull and stake agreements at $200', 'cull_and_2x']
      ].map(([label, key]) => { const v = agreement.historical.scenarios[key]; return <tr role="row" key={key}><td role="cell" data-label="Scenario">{label}</td><td role="cell" data-label="Settled bets">{v?.n ?? 0}</td><td role="cell" data-label="Turnover">{money(v?.turnover ?? 0)}</td><td role="cell" data-label="Profit">{money(v?.pnl ?? 0)}</td><td role="cell" data-label="ROI">{pct(v?.roi_pct ?? null)}</td></tr>; })}</tbody></table>
      {agreement.prospective.settled > 0 && <p className="rc-note">Forward settled: all Core {pct(agreement.prospective.scenarios.core_all?.roi_pct ?? null)} on {agreement.prospective.scenarios.core_all?.n ?? 0} bets; agreement cull {pct(agreement.prospective.scenarios.cull_to_agreement?.roi_pct ?? null)} on {agreement.prospective.scenarios.cull_to_agreement?.n ?? 0} bets. See the turnover and stake scenarios above for the historical baseline.</p>}
      {agreement.bets.some(b => b.sample_role === 'PROSPECTIVE') && <table role="table" className="sr-table mobile-card-table">
        <thead role="rowgroup"><tr role="row"><th role="columnheader">Date</th><th role="columnheader">Race</th><th role="columnheader">Core runner</th><th role="columnheader">Brain runner</th><th role="columnheader">Read</th><th role="columnheader">Result</th></tr></thead>
        <tbody role="rowgroup">{agreement.bets.filter(b => b.sample_role === 'PROSPECTIVE').map((b,i) => <tr role="row" key={i}>
          <td role="cell" data-label="Date">{b.date}</td><td role="cell" data-label="Race">{b.race_id.split('|').slice(1).join(' R')}</td><td role="cell" data-label="Core runner">{b.runner}</td>
          <td role="cell" data-label="Brain runner">{b.brain_runner || '-'}</td><td role="cell" data-label="Read">{b.agreement.replace(/_/g,' ').toLowerCase()}</td><td role="cell" data-label="Result">{b.result}</td>
        </tr>)}</tbody>
      </table>}
      <p className="rc-note">The forward record remains paper. No live Core bet is culled or resized by this panel.</p>
    </div>}
    <div className="sr-card" style={{ padding: 12 }}>
      <label>Bucket <select value={policy} onChange={e => setPolicy(e.target.value)}>
        <option value="">Both Brain merit buckets</option>
        <option value="ON_BASE|S1|GENUINE">On-Base S1 Genuine (Brain)</option>
        <option value="ON_BASE|S1|BOOTSTRAP">On-Base S1 Bootstrap (Brain)</option>
      </select></label>
    </div>
    <table role="table" className="sr-table mobile-card-table"><thead role="rowgroup"><tr role="row"><th role="columnheader">Paper bucket</th><th role="columnheader">Selections</th><th role="columnheader">Settled</th><th role="columnheader">Wins</th><th role="columnheader">Unresolved</th><th role="columnheader">Unit P&amp;L</th><th role="columnheader">ROI</th></tr></thead>
      <tbody role="rowgroup">{data?.buckets.map(b => <tr role="row" key={b.policy_id}><td role="cell" data-label="Paper bucket">{b.policy_id}</td><td role="cell" data-label="Selections">{b.n}</td><td role="cell" data-label="Settled">{b.settled_n}</td><td role="cell" data-label="Wins">{b.wins}</td><td role="cell" data-label="Unresolved">{b.unresolved_n}</td><td role="cell" data-label="Unit P&L">{b.pnl_units.toFixed(2)}</td><td role="cell" data-label="ROI">{pct(b.roi_pct)}</td></tr>)}</tbody>
    </table>
    {policy && <table role="table" className="sr-table mobile-card-table"><thead role="rowgroup"><tr role="row"><th role="columnheader">Date</th><th role="columnheader">Race</th><th role="columnheader">Runner</th><th role="columnheader">Brain</th><th role="columnheader">Core</th><th role="columnheader">Quote</th><th role="columnheader">Result</th><th role="columnheader">BSP</th><th role="columnheader">Unit P&amp;L</th></tr></thead>
      <tbody role="rowgroup">{data?.bets.map((b,i) => <tr role="row" key={i}><td role="cell" data-label="Date">{b.date}</td><td role="cell" data-label="Race">{b.race_id.split('|').slice(1).join(' R')}</td><td role="cell" data-label="Runner">{b.runner}</td><td role="cell" data-label="Brain">{(b.brain_prob*100).toFixed(1)}%</td><td role="cell" data-label="Core">{(b.core_prob*100).toFixed(1)}%</td><td role="cell" data-label="Quote">{price(b.quote)}</td><td role="cell" data-label="Result">{b.result}</td><td role="cell" data-label="BSP">{price(b.bsp)}</td><td role="cell" data-label="Unit P&L">{b.benchmark_pnl_units == null ? '-' : b.benchmark_pnl_units.toFixed(2)}</td></tr>)}</tbody>
    </table>}
  </section>;
};

const BucketBets: React.FC<{ personal?: boolean }> = ({ personal = false }) => {
  const [dimension, setDimension] = useState<'engine' | 'brain' | 'surface' | 'factors'>('engine');

  return (
    <MyBucketsProvider enabled={personal}><div className="sr-page bucket-workspace">
      <div className="sr-head">
        <h1>Bet buckets · past bets</h1>
      </div>

      <div className="bucket-tabs" style={{ display: 'flex', gap: 8, margin: '0 0 4px' }}>
        <button
          type="button"
          onClick={() => setDimension('engine')}
          className="sr-pill"
          style={{ border: 'none', cursor: 'pointer', background: dimension === 'engine' ? '#1e40af' : '#f3f4f6', color: dimension === 'engine' ? '#fff' : '#374151' }}
        >
          Engine buckets
        </button>
        <button type="button" onClick={() => setDimension('brain')} className="sr-pill" style={{ border: 'none', cursor: 'pointer', background: dimension === 'brain' ? '#1e40af' : '#f3f4f6', color: dimension === 'brain' ? '#fff' : '#374151' }}>Brain merit paper</button>
        <button
          type="button"
          onClick={() => setDimension('surface')}
          className="sr-pill"
          style={{ border: 'none', cursor: 'pointer', background: dimension === 'surface' ? '#1e40af' : '#f3f4f6', color: dimension === 'surface' ? '#fff' : '#374151' }}
        >
          Surface bands
        </button>
        <button
          type="button"
          onClick={() => setDimension('factors')}
          className="sr-pill"
          style={{ border: 'none', cursor: 'pointer', background: dimension === 'factors' ? '#1e40af' : '#f3f4f6', color: dimension === 'factors' ? '#fff' : '#374151' }}
        >
          Factor families
        </button>
      </div>

      {dimension === 'engine' ? <EngineBuckets personal={personal} /> : dimension === 'brain' ? <BrainMeritPaper /> : dimension === 'surface' ? <SurfaceBands /> : <FactorFamilies />}
      {dimension === 'engine' && <HistoricalTrackFilterReference />}
      {dimension === 'engine' && <TrackVenueRanking />}
    </div></MyBucketsProvider>
  );
};

export default BucketBets;
